import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AuthState, User } from '../types';
import { api, getToken, clearToken, getImpersonator, IMPERSONATE_KEY } from '../api';

interface AuthContextType extends AuthState {
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => void;
  impersonator: string | null;
}

// Admin "view as investor" links arrive as /#impersonate=<jwt>
function consumeImpersonationHash(): void {
  const match = window.location.hash.match(/^#impersonate=(.+)$/);
  if (!match) return;
  sessionStorage.setItem(IMPERSONATE_KEY, match[1]);
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    consumeImpersonationHash();
    return {
      user: null,
      token: getToken(),
      isAuthenticated: false,
      isLoading: !!getToken(),
      error: null,
      requires2fa: false,
    };
  });
  const [impersonator, setImpersonator] = useState<string | null>(() => getImpersonator());

  const credentialsRef = useRef<{ email: string; password: string } | null>(null);

  const login = useCallback(async (email: string, password: string, totpCode?: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const body: any = { username: email, password };
      if (totpCode) {
        body.totp_code = totpCode;
      }

      const res = await fetch((process.env.REACT_APP_API_URL || '') + '/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.requires_2fa) {
        credentialsRef.current = { email, password };
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          requires2fa: true,
          error: data.error || null,
        }));
        return;
      }

      if (!res.ok || !data.token) {
        throw new Error(data.message || 'Invalid credentials');
      }

      localStorage.setItem('amk_token', data.token);
      const user: User = await api.getMe();

      credentialsRef.current = null;
      setAuthState({
        user,
        token: data.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        requires2fa: false,
      });
    } catch (err: any) {
      localStorage.removeItem('amk_token');
      setAuthState(prev => ({
        ...prev,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: err.message || 'Invalid credentials',
      }));
    }
  }, []);

  const logout = useCallback(() => {
    if (getImpersonator()) {
      sessionStorage.removeItem(IMPERSONATE_KEY);
      setImpersonator(null);
      window.location.href = '/admin';
      return;
    }
    localStorage.removeItem('amk_token');
    credentialsRef.current = null;
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      requires2fa: false,
    });
  }, []);

  // On mount: validate existing token
  React.useEffect(() => {
    const token = getToken();
    if (!token) return;

    api.getMe()
      .then((user: User) => {
        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          requires2fa: false,
        });
      })
      .catch(() => {
        clearToken();
        setImpersonator(getImpersonator());
        setAuthState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          requires2fa: false,
        });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose credentials for 2FA retry
  const loginWithTotp = useCallback(async (email: string, password: string, totpCode?: string) => {
    if (totpCode && credentialsRef.current) {
      return login(credentialsRef.current.email, credentialsRef.current.password, totpCode);
    }
    return login(email, password, totpCode);
  }, [login]);

  return (
    <AuthContext.Provider value={{ ...authState, login: loginWithTotp, logout, impersonator }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
